import { useEffect, useMemo, useState } from "react";
import { useContracts } from "@/hooks/contracts";
import { useAvailableProducts } from "@/hooks/products";
import { useCustomerQueryScope } from "@/hooks/useCustomerScopedQuery";
import { isLicensedContract } from "@/utils/contractHelpers";
function dateValue(value) {
    const parsed = new Date(value).getTime();
    return Number.isNaN(parsed) ? Number.MIN_SAFE_INTEGER : parsed;
}
function useUsageContractSelection({ authProductCode, enabled = true, selectedContractId, setSelectedContractId }) {
    const scope = useCustomerQueryScope();
    const { data: contractsData, isLoading, error, isPlaceholderData } = useContracts(undefined, enabled);
    const { data: productsData } = useAvailableProducts(enabled);
    const [resolvedScopeKey, setResolvedScopeKey] = useState(null);
    const options = useMemo(()=>{
        const productNames = new Map(productsData?.products?.map((product)=>[
                product.productCode,
                product.productName
            ]) ?? []);
        const contracts = contractsData?.contracts.filter(({ contract })=>!isLicensedContract(contract)) ?? [];
        const productCounts = new Map();
        const now = Date.now();
        for (const { contract } of contracts){
            productCounts.set(contract.ProductCode, (productCounts.get(contract.ProductCode) ?? 0) + 1);
        }
        return contracts.map((contractDetails)=>{
            const { contract } = contractDetails;
            const expiryTime = new Date(contract.ExpiryDate).getTime();
            const isExpired = Number.isNaN(expiryTime) || expiryTime <= now;
            const productLabel = productNames.get(contract.ProductCode) ?? contract.ProductCode;
            const needsContractLabel = (productCounts.get(contract.ProductCode) ?? 0) > 1;
            const contractLabel = contract.Name?.trim() || `${contract.ContractId.slice(0, 8)}${contract.ContractId.length > 8 ? "..." : ""}`;
            const labelParts = [
                productLabel,
                ...needsContractLabel ? [
                    `- ${contractLabel}`
                ] : [],
                ...scope.isViewingAllOrgs ? [
                    `- ${contract.CustomerId}`
                ] : [],
                ...isExpired ? [
                    `(Expired ${contract.ExpiryDate.slice(0, 10)})`
                ] : []
            ];
            return {
                contractDetails,
                isExpired,
                label: labelParts.join(" "),
                value: contract.ContractId
            };
        }).sort((left, right)=>{
            if (left.isExpired !== right.isExpired) {
                return left.isExpired ? 1 : -1;
            }
            const productComparison = left.contractDetails.contract.ProductCode.localeCompare(right.contractDetails.contract.ProductCode);
            if (productComparison !== 0) {
                return productComparison;
            }
            const expiryComparison = dateValue(right.contractDetails.contract.ExpiryDate) - dateValue(left.contractDetails.contract.ExpiryDate);
            if (expiryComparison !== 0) {
                return expiryComparison;
            }
            return left.contractDetails.contract.ContractId.localeCompare(right.contractDetails.contract.ContractId);
        });
    }, [
        contractsData?.contracts,
        productsData?.products,
        scope.isViewingAllOrgs
    ]);
    useEffect(()=>{
        if (!contractsData || isPlaceholderData) {
            return;
        }
        setResolvedScopeKey(scope.queryKey);
        const selectedIsAvailable = options.some(({ contractDetails })=>contractDetails.contract.ContractId === selectedContractId);
        if (selectedIsAvailable) {
            return;
        }
        const preferredActive = options.find(({ contractDetails, isExpired })=>!isExpired && contractDetails.contract.ProductCode === authProductCode);
        const firstActive = options.find(({ isExpired })=>!isExpired);
        setSelectedContractId(preferredActive?.contractDetails.contract.ContractId ?? firstActive?.contractDetails.contract.ContractId ?? options[0]?.contractDetails.contract.ContractId ?? "");
    }, [
        authProductCode,
        contractsData,
        isPlaceholderData,
        options,
        scope.queryKey,
        selectedContractId,
        setSelectedContractId
    ]);
    const selectedOption = resolvedScopeKey === scope.queryKey ? options.find(({ contractDetails })=>contractDetails.contract.ContractId === selectedContractId) : undefined;
    return {
        activeOptions: options.filter(({ isExpired })=>!isExpired),
        error,
        expiredOptions: options.filter(({ isExpired })=>isExpired),
        isLoading,
        resolvedSelectedContractId: selectedOption?.contractDetails.contract.ContractId ?? "",
        selectedContract: selectedOption?.contractDetails.contract,
        selectedContractDetails: selectedOption?.contractDetails,
        selectedIsExpired: selectedOption?.isExpired ?? false
    };
}
export { useUsageContractSelection };
